import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Phone, Mail, Home, User, Save, Trash2, UserPlus } from 'lucide-react';
import { useState } from 'react';
import { useAuthStore } from '@/stores/auth';
import { leads, agents, type Lead, type Agent } from '@/lib/api';
import { formatDateTime, cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { PipelineStages, StageDropdown, StageHistory } from '@/components/pipeline';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const statusOptions = [
  { value: 'nýr', label: 'Nýr' },
  { value: 'haft_samband', label: 'Haft samband' },
  { value: 'bókað', label: 'Bókað' },
  { value: 'lokað', label: 'Lokað' },
  { value: 'glatað', label: 'Glatað' },
];

export default function LeadDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { token, user } = useAuthStore();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const isAdmin = user?.role === 'admin';

  const { data: lead, isLoading } = useQuery({
    queryKey: ['lead', id],
    queryFn: () => leads.get(token!, id!),
    enabled: !!token && !!id,
  });

  // Fetch agents for assignment dropdown
  const { data: agentsData } = useQuery({
    queryKey: ['agents'],
    queryFn: () => agents.list(token!, false), // Only active agents
    enabled: !!token,
  });

  const activeAgents = agentsData?.data?.filter((agent: Agent) => agent.active && agent.accepts_leads) || [];

  const [editedLead, setEditedLead] = useState<Partial<Lead>>({});
  const [isEditing, setIsEditing] = useState(false);

  const updateMutation = useMutation({
    mutationFn: (data: Partial<Lead>) => leads.update(token!, id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead', id] });
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      setIsEditing(false);
      setEditedLead({});
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => leads.delete(token!, id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      toast({
        title: 'Lead eytt',
        description: 'Lead hefur verið eytt.',
      });
      navigate('/leads');
    },
    onError: (error: Error) => {
      toast({
        title: 'Villa',
        description: error.message || 'Ekki tókst að eyða lead.',
        variant: 'destructive',
      });
    },
  });

  // Agent assignment mutation
  const assignAgentMutation = useMutation({
    mutationFn: (agentId: string | null) =>
      leads.update(token!, id!, { assigned_agent_id: agentId || undefined }),
    onSuccess: (_, agentId) => {
      queryClient.invalidateQueries({ queryKey: ['lead', id] });
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      const agentName = agentId
        ? activeAgents.find((a: Agent) => a.id === agentId)?.name
        : null;
      toast({
        title: agentId ? 'Söluaðila úthlutað' : 'Úthlutun fjarlægð',
        description: agentId
          ? `${agentName || 'Söluaðili'} hefur verið úthlutað á þennan lead.`
          : 'Söluaðili hefur verið fjarlægður af þessum lead.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Villa',
        description: error.message || 'Ekki tókst að úthluta söluaðila.',
        variant: 'destructive',
      });
    },
  });

  const handleAgentAssignment = (value: string) => {
    const agentId = value === 'none' ? null : value;
    assignAgentMutation.mutate(agentId);
  };

  const handleStageChange = async (newStage: string, _reason?: string) => {
    try {
      await leads.update(token!, id!, { status: newStage });
      queryClient.invalidateQueries({ queryKey: ['lead', id] });
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      toast({
        title: 'Staða uppfærð',
        description: `Lead hefur verið færður í "${newStage}".`,
      });
    } catch (error) {
      toast({
        title: 'Villa',
        description: error instanceof Error ? error.message : 'Ekki tókst að uppfæra stöðu.',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = () => {
    setShowDeleteDialog(false);
    deleteMutation.mutate();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-prima-600" />
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Lead fannst ekki</p>
        <Link to="/leads" className="text-prima-600 hover:underline mt-2 inline-block">
          ← Til baka í leads
        </Link>
      </div>
    );
  }

  const currentValues = { ...lead, ...editedLead };

  const handleSave = () => {
    if (Object.keys(editedLead).length > 0) {
      updateMutation.mutate(editedLead);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/leads')}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">
            {lead.caller_name || 'Nafnlaus lead'}
          </h1>
          <p className="text-gray-600">
            Stofnað {formatDateTime(lead.created_at)}
          </p>
        </div>
        {isEditing && (
          <button
            onClick={handleSave}
            disabled={updateMutation.isPending}
            className="btn-primary"
          >
            <Save className="w-4 h-4 mr-2" />
            {updateMutation.isPending ? 'Vista...' : 'Vista'}
          </button>
        )}
        {isAdmin && (
          <button
            onClick={() => setShowDeleteDialog(true)}
            disabled={deleteMutation.isPending}
            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title="Eyda lead"
          >
            <Trash2 className="w-5 h-5" />
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Contact info */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Tengiliðaupplýsingar</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="label">Nafn</label>
                <input
                  type="text"
                  value={currentValues.caller_name || ''}
                  onChange={(e) => {
                    setIsEditing(true);
                    setEditedLead({ ...editedLead, caller_name: e.target.value });
                  }}
                  className="input"
                  placeholder="Nafn viðskiptavinar"
                />
              </div>
              <div>
                <label className="label">Sími</label>
                <div className="flex items-center gap-2">
                  <input
                    type="tel"
                    value={currentValues.caller_phone}
                    onChange={(e) => {
                      setIsEditing(true);
                      setEditedLead({ ...editedLead, caller_phone: e.target.value });
                    }}
                    className="input flex-1"
                  />
                  <a
                    href={`tel:${lead.caller_phone}`}
                    className="p-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200"
                  >
                    <Phone className="w-5 h-5" />
                  </a>
                </div>
              </div>
              <div className="sm:col-span-2">
                <label className="label">Netfang</label>
                <div className="flex items-center gap-2">
                  <input
                    type="email"
                    value={currentValues.caller_email || ''}
                    onChange={(e) => {
                      setIsEditing(true);
                      setEditedLead({ ...editedLead, caller_email: e.target.value });
                    }}
                    className="input flex-1"
                    placeholder="netfang@example.is"
                  />
                  {lead.caller_email && (
                    <a
                      href={`mailto:${lead.caller_email}`}
                      className="p-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
                    >
                      <Mail className="w-5 h-5" />
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Athugasemdir</h2>
            <textarea
              value={currentValues.notes || ''}
              onChange={(e) => {
                setIsEditing(true);
                setEditedLead({ ...editedLead, notes: e.target.value });
              }}
              rows={4}
              className="input"
              placeholder="Bæta við athugasemdum..."
            />
          </div>

          {/* Pipeline Section */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Framvinda</h2>
            <div className="space-y-6">
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">Núverandi staða</h3>
                <PipelineStages
                  currentStage={lead.status}
                />
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">Breyta stöðu</h3>
                <StageDropdown
                  leadId={lead.id}
                  currentStage={lead.status}
                  onStageChange={handleStageChange}
                />
              </div>
            </div>
          </div>

          {/* History Section */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Saga</h2>
            <StageHistory leadId={lead.id} />
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Status & Score */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Staða</h2>
            <div className="space-y-4">
              <div>
                <label className="label">Staða</label>
                <select
                  value={currentValues.status}
                  onChange={(e) => {
                    setIsEditing(true);
                    setEditedLead({ ...editedLead, status: e.target.value });
                  }}
                  className="input"
                >
                  {statusOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">Lead stig</label>
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={currentValues.lead_score}
                    onChange={(e) => {
                      setIsEditing(true);
                      setEditedLead({ ...editedLead, lead_score: parseInt(e.target.value) });
                    }}
                    className="flex-1"
                  />
                  <span
                    className={cn(
                      'w-10 h-10 rounded-full flex items-center justify-center text-white font-medium text-sm',
                      currentValues.lead_score >= 80 ? 'bg-green-500' :
                      currentValues.lead_score >= 50 ? 'bg-yellow-500' : 'bg-gray-400'
                    )}
                  >
                    {currentValues.lead_score}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Property */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Eign</h2>
            {lead.property_address ? (
              <div className="flex items-start gap-3">
                <Home className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="font-medium text-gray-900">{lead.property_address}</p>
                  {lead.property_id && (
                    <Link
                      to={`/properties/${lead.property_id}`}
                      className="text-sm text-prima-600 hover:underline"
                    >
                      Sjá eign →
                    </Link>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-gray-500 text-sm">Engin eign tengd</p>
            )}
          </div>

          {/* Agent Assignment */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              <span className="flex items-center gap-2">
                <UserPlus className="w-5 h-5" />
                Úthluta söluaðila
              </span>
            </h2>
            <div className="space-y-3">
              <Select
                value={lead.assigned_agent_id || 'none'}
                onValueChange={handleAgentAssignment}
                disabled={assignAgentMutation.isPending}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Veldu söluaðila..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">
                    <span className="text-gray-500">Enginn söluaðili</span>
                  </SelectItem>
                  {activeAgents.map((agent: Agent) => (
                    <SelectItem key={agent.id} value={agent.id}>
                      <span className="flex items-center gap-2">
                        <User className="w-4 h-4 text-gray-400" />
                        {agent.name}
                        {agent.specialization && (
                          <span className="text-xs text-gray-500">
                            ({agent.specialization})
                          </span>
                        )}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {assignAgentMutation.isPending && (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-prima-600" />
                  Úthluta...
                </div>
              )}

              {lead.assigned_agent_id && lead.agent_name && (
                <div className="pt-2 border-t border-gray-100">
                  <div className="flex items-start gap-3">
                    <User className="w-5 h-5 text-prima-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-gray-900">{lead.agent_name}</p>
                      <Link
                        to={`/agents/${lead.assigned_agent_id}`}
                        className="text-sm text-prima-600 hover:underline"
                      >
                        Sjá söluaðila →
                      </Link>
                    </div>
                  </div>
                </div>
              )}

              {activeAgents.length === 0 && (
                <p className="text-sm text-gray-500">
                  Engir virkir söluaðilar til að úthluta.{' '}
                  <Link to="/agents" className="text-prima-600 hover:underline">
                    Bæta við söluaðila →
                  </Link>
                </p>
              )}
            </div>
          </div>

          {/* Source */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Uppruni</h2>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Tegund</span>
                <span className="font-medium">{lead.inquiry_type || 'Óþekkt'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Uppruni</span>
                <span className="font-medium capitalize">{lead.source}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Stofnað</span>
                <span className="font-medium">{formatDateTime(lead.created_at)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Uppfært</span>
                <span className="font-medium">{formatDateTime(lead.updated_at)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eyda lead?</AlertDialogTitle>
            <AlertDialogDescription>
              Ertu viss um að þú viljir eyða þessum lead? Þetta er ekki hægt að afturkalla.
              {lead.caller_name && (
                <span className="block mt-2 font-medium text-gray-900">
                  Lead: {lead.caller_name}
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>
              Hætta við
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {deleteMutation.isPending ? 'Eydi...' : 'Eyda'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
